/**
 * Shared utility to build a complete optimizer request from community data.
 *
 * Replicates the data-loading logic from ModellingPage so that any page
 * (dashboard, what-if planning, etc.) can call the optimizer with the same
 * high-quality input assembly: dataset values → saved forecasts → synthetic.
 *
 * Optionally applies what-if parameters (load/tariff scaling, new assets)
 * on top of the real community data.
 */

import { getValues, getDatasets } from '../api/datasets'
import { resultsAPI } from '../api/results'
import {
  czechLoadProfile,
  czechPVProfile,
  getSeason,
  estimateBuildingAnnualKwh,
} from '../data/czechEnergyProfiles'
import { isCommunityPV } from '../schemas/communitySchema'

/**
 * Load 24 hourly values from a dataset, matching the target month/day
 * in the 2025 dataset year for seasonal representativeness.
 */
async function loadHourlyValues(datasetId, targetDate) {
  if (!datasetId) return null
  const dateStr = targetDate || new Date().toISOString().split('T')[0]

  try {
    const tgt = new Date(dateStr + 'T00:00:00Z')
    const datasetYear = 2025
    const startISO = `${datasetYear}-${String(tgt.getUTCMonth() + 1).padStart(2, '0')}-${String(tgt.getUTCDate()).padStart(2, '0')}T00:00:00Z`
    const endDate = new Date(startISO)
    endDate.setUTCHours(23, 59, 59)
    const endISO = endDate.toISOString()

    const resp = await getValues(datasetId, {
      start_time: startISO,
      end_time: endISO,
      limit: 24,
    })
    const rawValues = resp?.values || (Array.isArray(resp) ? resp : [])
    if (rawValues.length >= 24) {
      return rawValues.slice(0, 24).map(v =>
        typeof v === 'number' ? v : (v?.value ?? v?.consumption_kwh ?? 0)
      )
    }

    // Fallback: try without date filter
    if (rawValues.length < 24) {
      const fallbackResp = await getValues(datasetId, { limit: 24 })
      const fallbackValues = fallbackResp?.values || (Array.isArray(fallbackResp) ? fallbackResp : [])
      if (fallbackValues.length >= 24) {
        return fallbackValues.slice(0, 24).map(v =>
          typeof v === 'number' ? v : (v?.value ?? v?.consumption_kwh ?? 0)
        )
      }
    }
  } catch (e) {
    console.warn(`Failed to load values from dataset ${datasetId}:`, e.message)
  }
  return null
}

/**
 * Load 24 hourly values from a day-ahead dataset using the ACTUAL target date
 * (not mapped to 2025). Day-ahead datasets store values with real-world timestamps.
 */
async function loadDayAheadValues(datasetId, targetDate) {
  if (!datasetId) return null
  const dateStr = targetDate || new Date().toISOString().split('T')[0]

  try {
    const startISO = `${dateStr}T00:00:00Z`
    const endDate = new Date(startISO)
    endDate.setUTCHours(23, 59, 59)
    const endISO = endDate.toISOString()

    const resp = await getValues(datasetId, {
      start_time: startISO,
      end_time: endISO,
      limit: 24,
    })
    const rawValues = resp?.values || (Array.isArray(resp) ? resp : [])
    if (rawValues.length >= 24) {
      return rawValues.slice(0, 24).map(v =>
        typeof v === 'number' ? v : (v?.value ?? v?.consumption_kwh ?? 0)
      )
    }
  } catch (e) {
    console.warn(`Failed to load day-ahead values from dataset ${datasetId}:`, e.message)
  }
  return null
}

/**
 * Compute seasonal hourly average (24 values) from a historical tariff dataset.
 * Fetches a ~1-week window around the current season's midpoint and averages by hour.
 */
async function loadHistoricalTariffAverage(datasetId) {
  if (!datasetId) return null

  try {
    const season = getSeason()
    const midMonths = {
      winter: { month: 0, day: 15 },
      spring: { month: 3, day: 15 },
      summer: { month: 6, day: 15 },
      fall:   { month: 9, day: 15 },
    }
    const mid = midMonths[season] || midMonths.spring
    const start = new Date(2025, mid.month, mid.day - 3)
    const end = new Date(2025, mid.month, mid.day + 4)

    const resp = await getValues(datasetId, {
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      limit: 168,  // 7 days * 24 hours
    })
    const rawValues = resp?.values || (Array.isArray(resp) ? resp : [])
    if (rawValues.length < 24) return null

    // Average by hour-of-day
    const sums = new Array(24).fill(0)
    const counts = new Array(24).fill(0)
    for (const v of rawValues) {
      const val = typeof v === 'number' ? v : (v?.value ?? 0)
      const hour = v?.time ? new Date(v.time).getUTCHours() : null
      if (hour != null && val != null && !isNaN(val)) {
        sums[hour] += val
        counts[hour] += 1
      }
    }
    const avg = sums.map((s, i) => counts[i] > 0 ? +(s / counts[i]).toFixed(4) : 0)
    // Verify we got meaningful data (at least some non-zero)
    if (avg.some(v => v > 0)) return avg
  } catch (e) {
    console.warn(`Failed to compute historical tariff average from dataset ${datasetId}:`, e.message)
  }
  return null
}

/**
 * Build a complete optimizer request for a community.
 *
 * @param {Object} params
 * @param {Object} params.community      - Full community object (with buildings, community_assets, community_datasets)
 * @param {string} params.communityId     - Community UUID
 * @param {string} params.objective       - 'min_cost' | 'max_self_sufficiency' | 'max_self_consumption' | 'min_co2'
 * @param {Object} [params.whatIfParams]  - Optional what-if overrides
 * @param {number} [params.whatIfParams.loadGrowthPct]           - % to scale all building loads (e.g. 10 → ×1.10)
 * @param {number} [params.whatIfParams.tariffChangePct]         - % to scale all tariffs
 * @param {boolean} [params.whatIfParams.addPvSystem]            - Add new community PV
 * @param {number} [params.whatIfParams.newPvCapacityKwp]        - Capacity of new PV (kWp)
 * @param {boolean} [params.whatIfParams.addWindTurbine]         - Add new community wind
 * @param {number} [params.whatIfParams.newWindCapacityKw]       - Capacity of new wind (kW)
 * @param {boolean} [params.whatIfParams.addBattery]             - Add/augment community battery
 * @param {number} [params.whatIfParams.newBatteryCapacityKwh]   - New battery capacity
 * @param {number} [params.whatIfParams.newBatteryChargeRateKw]  - New battery charge rate
 * @param {number} [params.whatIfParams.newBatteryDischargeRateKw] - New battery discharge rate
 * @param {number} [params.whatIfParams.newBatteryEfficiencyPct] - New battery round-trip efficiency %
 * @returns {Promise<Object>} Full optimizer request object ready for POST /v1/model/optimize
 */
export async function buildOptimizerRequest({ community, communityId, objective, whatIfParams }) {
  const buildings = community?.buildings || []
  const communityAssets = community?.community_assets || []
  const currency = community?.currency || 'EUR'

  const tomorrow = (() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().split('T')[0]
  })()

  // ------------------------------------------------------------------
  // Load all datasets for the community
  // ------------------------------------------------------------------
  let allDatasets = []
  try {
    const dsResp = await getDatasets({ community_id: communityId })
    allDatasets = Array.isArray(dsResp) ? dsResp : []
  } catch (e) {
    console.warn('Could not fetch dataset list:', e.message)
  }

  // ------------------------------------------------------------------
  // Load saved forecast results as fallback
  // ------------------------------------------------------------------
  const savedLoadForecasts = {}
  const savedPVForecasts = {}
  const savedWindForecasts = {}
  try {
    const savedResults = await resultsAPI.list(communityId)
    const allResults = Array.isArray(savedResults) ? savedResults : []

    const loadResults = allResults.filter(r =>
      r.result_type === 'forecast' && r.parameters?.forecast_type === 'load' && r.parameters?.forecast_mode === 'short_term'
    )
    for (const lr of loadResults) {
      const assetName = lr.parameters?.asset
      if (!assetName || savedLoadForecasts[assetName]) continue
      try {
        const full = await resultsAPI.get(communityId, lr.id)
        if (full?.result_data?.forecast?.length >= 24) {
          savedLoadForecasts[assetName] = full.result_data.forecast.slice(0, 24)
        }
      } catch (e) { /* skip */ }
    }

    const genResults = allResults.filter(r =>
      r.result_type === 'forecast' && r.parameters?.forecast_type === 'generation' && r.parameters?.forecast_mode === 'short_term'
    )
    for (const gr of genResults) {
      const assetName = gr.parameters?.asset
      const category = gr.parameters?.asset_category || 'pv'
      if (!assetName) continue
      const targetMap = category === 'wind' ? savedWindForecasts : savedPVForecasts
      if (targetMap[assetName]) continue
      try {
        const full = await resultsAPI.get(communityId, gr.id)
        if (full?.result_data?.forecast?.length >= 24) {
          targetMap[assetName] = full.result_data.forecast.slice(0, 24)
        }
      } catch (e) { /* skip */ }
    }
  } catch (err) {
    console.warn('Could not load saved forecast results:', err.message)
  }

  // ------------------------------------------------------------------
  // Build per-building optimizer input
  // ------------------------------------------------------------------
  const perBuildingKwp = (() => {
    const totalKwp = communityAssets
      .filter(a => isCommunityPV(a))
      .reduce((sum, a) => sum + (a.specs?.capacity_kwp || 0), 0)
    return totalKwp / Math.max(1, buildings.length)
  })()

  const optimizerBuildings = await Promise.all(buildings.map(async (b, idx) => {
    const bid = b.building_id || b.id || `building_${idx}`
    const bAssets = b.building_assets || []

    // --- Load profile ---
    const buildingDatasets = b.datasets || []
    const loadDataset = buildingDatasets.find(d => d.kind === 'load' || d.dataset_type?.includes('load'))
      || allDatasets.find(d => d.building_id === b.id && (d.kind === 'load' || d.dataset_type?.includes('load')))

    let loadProfile = await loadHourlyValues(loadDataset?.id, tomorrow)
    if (!loadProfile && savedLoadForecasts[bid]) {
      loadProfile = savedLoadForecasts[bid]
    } else if (!loadProfile && savedLoadForecasts['Entire Community']) {
      loadProfile = savedLoadForecasts['Entire Community'].map(v => +(v / Math.max(1, buildings.length)).toFixed(2))
    } else if (!loadProfile) {
      const buildingAnnualKwh = estimateBuildingAnnualKwh(b, community)
      const buildingDailyKwh = buildingAnnualKwh / 365
      loadProfile = czechLoadProfile(buildingDailyKwh, 42 + idx, community?.metadata?.country)
    }

    // --- PV profile ---
    const rooftopPV = bAssets.find(a => a.asset_type === 'rooftop_pv')
    const buildingKwp = rooftopPV?.specs?.capacity_kwp || perBuildingKwp
    let pvProfile = null
    if (rooftopPV) {
      const pvDataset = buildingDatasets.find(d => d.kind === 'pv' || d.dataset_type?.includes('pv'))
        || allDatasets.find(d => d.building_id === b.id && (d.kind === 'pv' || d.dataset_type?.includes('pv')))
      pvProfile = await loadHourlyValues(pvDataset?.id, tomorrow)
      if (!pvProfile) {
        const rooftopName = `${bid} Rooftop PV`
        if (savedPVForecasts[rooftopName]) pvProfile = savedPVForecasts[rooftopName]
      }
      if (!pvProfile) {
        pvProfile = czechPVProfile(buildingKwp, getSeason(tomorrow), 43 + idx, community?.metadata?.latitude)
      }
    }

    const entry = { building_id: bid, load_kwh: loadProfile, pv_gen_kw: pvProfile }

    // --- Battery ---
    const battAsset = bAssets.find(a => a.asset_type === 'battery')
    if (battAsset) {
      const s = battAsset.specs || {}
      entry.battery = {
        capacity_kwh: s.capacity_kwh || 10,
        p_charge_kw: s.p_charge_kw || s.max_charge_kw || 3,
        p_discharge_kw: s.p_discharge_kw || s.max_discharge_kw || 3,
        efficiency_charge: s.efficiency_charge || 0.95,
        efficiency_discharge: s.efficiency_discharge || 0.95,
        soc_min_pct: s.soc_min_pct || 10,
        soc_max_pct: s.soc_max_pct || 90,
        initial_soc_pct: 50,
      }
    }

    // --- Heat pump ---
    const isControllable = (a) => a.specs?.controllable !== 'no'
    const hpAsset = bAssets.find(a => a.asset_type === 'heat_pump' && isControllable(a))
    if (hpAsset) {
      const s = hpAsset.specs || {}
      entry.heat_pump = {
        rated_power_kw: s.capacity_kw || s.rated_power_kw || 3,
        cop: s.cop || 3.0,
        eer: s.eer || 2.5,
        operating_mode: s.operating_mode || s.mode || 'heating',
        thermal_mass: s.thermal_mass || s.thermal_mass_kwh_per_c || 'medium',
        min_comfort_temp: s.min_comfort_temp || s.t_min_c || 20,
        max_comfort_temp: s.max_comfort_temp || s.t_max_c || 24,
        initial_indoor_temp: s.initial_indoor_temp || s.t_initial_c || 21,
        controllable: true,
      }
    }

    // --- HVAC ---
    const hvacAsset = bAssets.find(a => a.asset_type === 'hvac' && isControllable(a))
    if (hvacAsset && !hpAsset) {
      const s = hvacAsset.specs || {}
      entry.hvac = {
        rated_power_kw: s.capacity_kw || s.rated_power_kw || 5,
        cop: s.cop || s.cop_heating || 2.5,
        eer: s.eer || s.cop_cooling || 2.0,
        operating_mode: s.operating_mode || s.mode || 'heating',
        thermal_mass: s.thermal_mass || s.thermal_mass_kwh_per_c || 'medium',
        min_comfort_temp: s.min_comfort_temp || s.t_min_c || 20,
        max_comfort_temp: s.max_comfort_temp || s.t_max_c || 24,
        initial_indoor_temp: s.initial_indoor_temp || s.t_initial_c || 21,
        controllable: true,
      }
    }

    // --- EV charger ---
    const evAsset = bAssets.find(a => a.asset_type === 'ev_charger' && isControllable(a))
    if (evAsset) {
      const s = evAsset.specs || {}
      entry.ev_charger = {
        max_power_kw: s.max_power_kw || 11,
        num_evs: s.num_evs || 1,
        daily_energy_per_ev_kwh: s.daily_energy_kwh || s.daily_energy_per_ev_kwh || 25,
        availability_start: s.availability_start || s.arrival_hour || 18,
        availability_end: s.availability_end || s.departure_hour || 8,
        ev_battery_capacity_kwh: s.ev_battery_capacity_kwh || s.battery_capacity_kwh || 60,
      }
    }

    return entry
  }))

  // ------------------------------------------------------------------
  // Community-level assets
  // ------------------------------------------------------------------
  const pvFarm = communityAssets.find(a => isCommunityPV(a))
  const windTurbine = communityAssets.find(a => a.asset_type === 'wind_turbine')
  const commBattery = communityAssets.find(a => a.asset_type === 'community_battery')

  let communityPV = null
  if (pvFarm) {
    const farmKwp = pvFarm.specs?.capacity_kwp || 0
    const pvDataset = allDatasets.find(d =>
      !d.building_id && (d.kind === 'pv' || d.dataset_type?.includes('pv_generation'))
    )
    let pvGenProfile = await loadHourlyValues(pvDataset?.id, tomorrow)
    if (!pvGenProfile) {
      const farmName = pvFarm.name || 'Solar PV Farm'
      if (savedPVForecasts[farmName]) {
        pvGenProfile = savedPVForecasts[farmName]
      } else {
        const allPVArrays = Object.values(savedPVForecasts)
        if (allPVArrays.length > 0) {
          pvGenProfile = allPVArrays[0].map((_, t) =>
            +allPVArrays.reduce((sum, arr) => sum + (arr[t] || 0), 0).toFixed(2)
          )
        }
      }
    }
    if (!pvGenProfile) {
      pvGenProfile = czechPVProfile(farmKwp, getSeason(tomorrow), 99, community?.metadata?.latitude)
    }
    communityPV = { capacity_kwp: farmKwp, gen_kw: pvGenProfile }
  }

  let communityWind = null
  if (windTurbine) {
    const windKw = windTurbine.specs?.capacity_kw || 0
    const windDataset = allDatasets.find(d =>
      !d.building_id && (d.kind === 'wind' || d.dataset_type?.includes('wind_generation'))
    )
    let windGenProfile = await loadHourlyValues(windDataset?.id, tomorrow)
    if (!windGenProfile) {
      const turbineName = windTurbine.name || 'Wind Turbine'
      if (savedWindForecasts[turbineName]) {
        windGenProfile = savedWindForecasts[turbineName]
      } else {
        const allWindArrays = Object.values(savedWindForecasts)
        if (allWindArrays.length > 0) {
          windGenProfile = allWindArrays[0].map((_, t) =>
            +allWindArrays.reduce((sum, arr) => sum + (arr[t] || 0), 0).toFixed(2)
          )
        }
      }
    }
    if (!windGenProfile) {
      windGenProfile = Array.from({ length: 24 }, (_, t) =>
        parseFloat((windKw * (0.3 + 0.2 * Math.sin(t * 0.5))).toFixed(2))
      )
    }
    communityWind = { capacity_kw: windKw, gen_kw: windGenProfile }
  }

  let communityBattery = null
  if (commBattery) {
    const s = commBattery.specs || {}
    communityBattery = {
      capacity_kwh: s.capacity_kwh || 50,
      p_charge_kw: s.p_charge_kw || s.max_charge_kw || 15,
      p_discharge_kw: s.p_discharge_kw || s.max_discharge_kw || 15,
      efficiency_charge: s.efficiency_charge || 0.95,
      efficiency_discharge: s.efficiency_discharge || 0.95,
      soc_min_pct: s.soc_min_pct || 10,
      soc_max_pct: s.soc_max_pct || 90,
      initial_soc_pct: 50,
    }
  }

  // ------------------------------------------------------------------
  // Tariffs — 3-tier resolution:
  //   1. Day-ahead dataset (exact date match with real timestamps)
  //   2. Historical tariff dataset (seasonal hourly average)
  //   3. Static community metadata tariffs (flat rate with peak multiplier)
  // ------------------------------------------------------------------
  let tariffImport, tariffExport
  let tariffSource = 'none'
  const datasets = community?.community_datasets || community?.datasets || []

  // Identify available tariff datasets by type
  const dayAheadImportDsList = datasets.filter(d => d.dataset_type === 'day_ahead_import_tariff')
  const dayAheadExportDsList = datasets.filter(d => d.dataset_type === 'day_ahead_export_tariff')
  const historicalImportDs = datasets.find(d => d.dataset_type === 'energy_tariff_data' || (d.kind === 'tariff' && !d.dataset_type?.startsWith('day_ahead_') && d.dataset_type !== 'feed_in_tariff_data'))
  const historicalExportDs = datasets.find(d => d.dataset_type === 'feed_in_tariff_data')

  // Tier 1: Day-ahead tariffs (actual date, not 2025-mapped)
  // Try all day-ahead datasets until one has values for the target date
  try {
    for (const ds of dayAheadImportDsList) {
      const v = await loadDayAheadValues(ds.id, tomorrow)
      if (v && v.length >= 24) {
        tariffImport = v
        tariffSource = 'day_ahead'
        break
      }
    }
    for (const ds of dayAheadExportDsList) {
      const v = await loadDayAheadValues(ds.id, tomorrow)
      if (v && v.length >= 24) { tariffExport = v; break }
    }
  } catch (e) {
    console.warn('Could not load day-ahead tariff datasets:', e.message)
  }

  // Tier 2: Historical tariff datasets (seasonal hourly average)
  if (!tariffImport) {
    try {
      if (historicalImportDs?.id) {
        const v = await loadHistoricalTariffAverage(historicalImportDs.id)
        if (v && v.length >= 24) {
          tariffImport = v
          tariffSource = 'historical'
        }
      }
    } catch (e) {
      console.warn('Could not load historical import tariff:', e.message)
    }
  }
  if (!tariffExport) {
    try {
      if (historicalExportDs?.id) {
        const v = await loadHistoricalTariffAverage(historicalExportDs.id)
        if (v && v.length >= 24) tariffExport = v
      }
    } catch (e) {
      console.warn('Could not load historical export tariff:', e.message)
    }
  }

  // Tier 3: Static community metadata tariffs
  if (!tariffImport) {
    const meta = community?.metadata || {}
    const metaImport = meta.import_tariff
    if (metaImport && Number(metaImport) > 0) {
      const importRate = Number(metaImport) / (currency === 'CZK' ? 25 : 1)
      tariffImport = Array.from({ length: 24 }, (_, t) => {
        if ((t >= 7 && t <= 9) || (t >= 17 && t <= 21)) return parseFloat((importRate * 1.2).toFixed(4))
        return importRate
      })
      tariffSource = 'metadata'
    } else {
      console.error('Community import tariff not configured. Please update community settings.')
      tariffImport = Array(24).fill(0)
    }
  }
  if (!tariffExport) {
    const meta = community?.metadata || {}
    const metaExport = meta.export_tariff
    if (metaExport && Number(metaExport) > 0) {
      const exportRate = Number(metaExport) / (currency === 'CZK' ? 25 : 1)
      tariffExport = Array(24).fill(exportRate)
    } else {
      console.error('Community export tariff not configured. Please update community settings.')
      tariffExport = Array(24).fill(0)
    }
  }
  console.info(`Optimizer tariff source: ${tariffSource} (import avg: ${(tariffImport.reduce((a,b)=>a+b,0)/24).toFixed(4)}, export avg: ${(tariffExport.reduce((a,b)=>a+b,0)/24).toFixed(4)})`)

  // ------------------------------------------------------------------
  // CO2 factor & weather
  // ------------------------------------------------------------------
  const co2Factor = community?.metadata?.co2_factor_kg_kwh
    || (community?.metadata?.country === 'Italy' ? 0.233
      : community?.metadata?.country === 'Germany' ? 0.350
      : 0.4)

  let temperatureProfile = null
  const weatherDataset = allDatasets.find(d =>
    d.dataset_type?.includes('weather') || d.dataset_type?.includes('temperature')
  )
  if (weatherDataset?.id) {
    try {
      temperatureProfile = await loadHourlyValues(weatherDataset.id, tomorrow)
    } catch (e) {
      console.warn('Could not load weather data:', e.message)
    }
  }

  // ==================================================================
  // Apply what-if parameters
  // ==================================================================
  if (whatIfParams) {
    // 1. Load scaling: multiply all building loads
    const loadPct = whatIfParams.loadGrowthPct || 0
    if (loadPct !== 0) {
      const factor = 1 + loadPct / 100
      for (const b of optimizerBuildings) {
        if (b.load_kwh) {
          b.load_kwh = b.load_kwh.map(v => +(v * factor).toFixed(4))
        }
      }
    }

    // 2. Tariff scaling: multiply import and export tariffs
    const tariffPct = whatIfParams.tariffChangePct || 0
    if (tariffPct !== 0) {
      const factor = 1 + tariffPct / 100
      tariffImport = tariffImport.map(v => +(v * factor).toFixed(6))
      tariffExport = tariffExport.map(v => +(v * factor).toFixed(6))
    }

    // 3. New PV system: generate realistic profile using czechPVProfile
    if (whatIfParams.addPvSystem && whatIfParams.newPvCapacityKwp > 0) {
      const newKwp = whatIfParams.newPvCapacityKwp
      const newPvProfile = czechPVProfile(
        newKwp,
        getSeason(tomorrow),
        77, // distinct seed for what-if PV
        community?.metadata?.latitude
      )
      if (communityPV) {
        communityPV = {
          capacity_kwp: communityPV.capacity_kwp + newKwp,
          gen_kw: communityPV.gen_kw.map((v, i) => +(v + newPvProfile[i]).toFixed(4)),
        }
      } else {
        communityPV = { capacity_kwp: newKwp, gen_kw: newPvProfile }
      }
    }

    // 4. New wind turbine: generate profile with capacity factor ~0.25-0.35
    if (whatIfParams.addWindTurbine && whatIfParams.newWindCapacityKw > 0) {
      const newWindKw = whatIfParams.newWindCapacityKw
      const newWindProfile = Array.from({ length: 24 }, (_, t) =>
        parseFloat((newWindKw * (0.3 + 0.2 * Math.sin(t * 0.5))).toFixed(2))
      )
      if (communityWind) {
        communityWind = {
          capacity_kw: communityWind.capacity_kw + newWindKw,
          gen_kw: communityWind.gen_kw.map((v, i) => +(v + newWindProfile[i]).toFixed(4)),
        }
      } else {
        communityWind = { capacity_kw: newWindKw, gen_kw: newWindProfile }
      }
    }

    // 5. New battery: augment existing or create new
    if (whatIfParams.addBattery && whatIfParams.newBatteryCapacityKwh > 0) {
      const newKwh = whatIfParams.newBatteryCapacityKwh
      const newChargeKw = whatIfParams.newBatteryChargeRateKw || newKwh * 0.5
      const newDischargeKw = whatIfParams.newBatteryDischargeRateKw || newKwh * 0.5
      const newEff = (whatIfParams.newBatteryEfficiencyPct || 90) / 100

      if (communityBattery) {
        communityBattery = {
          capacity_kwh: communityBattery.capacity_kwh + newKwh,
          p_charge_kw: communityBattery.p_charge_kw + newChargeKw,
          p_discharge_kw: communityBattery.p_discharge_kw + newDischargeKw,
          efficiency_charge: (communityBattery.efficiency_charge + newEff) / 2,
          efficiency_discharge: (communityBattery.efficiency_discharge + newEff) / 2,
          soc_min_pct: communityBattery.soc_min_pct,
          soc_max_pct: communityBattery.soc_max_pct,
          initial_soc_pct: 50,
        }
      } else {
        communityBattery = {
          capacity_kwh: newKwh,
          p_charge_kw: newChargeKw,
          p_discharge_kw: newDischargeKw,
          efficiency_charge: newEff,
          efficiency_discharge: newEff,
          soc_min_pct: 10,
          soc_max_pct: 90,
          initial_soc_pct: 50,
        }
      }
    }
  }

  // ==================================================================
  // Assemble final request
  // ==================================================================
  return {
    horizon_hours: 24,
    time_resolution_hours: 1.0,
    objective: objective || 'min_cost',
    co2_factor_kg_kwh: co2Factor,
    buildings: optimizerBuildings,
    community_pv: communityPV,
    community_wind: communityWind,
    community_battery: communityBattery,
    tariff_import: tariffImport,
    tariff_export: tariffExport,
    scope: 'community',
    solver_time_limit_seconds: 30,
    season: getSeason(tomorrow),
    latitude: community?.metadata?.latitude || null,
    country: community?.metadata?.country || null,
    temperature_c: temperatureProfile || null,
  }
}
